import { Component } from '@theme/component';
import {
  debounce,
  isClickedOutside,
  lockScroll,
  onAnimationEnd,
  unlockScroll,
} from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  #previousScrollY = 0;
  #opening = false;
  #closing = false;
  #openFrame = null;
  #closeTimer = null;
  #closeVersion = 0;
  #sessionOpen = false;
  #dialog = null;

  connectedCallback() {
    super.connectedCallback();

    this.#dialog = this.refs.dialog;
    this.#dialog.addEventListener('close', this.#handleNativeClose);
    this.#dialog.addEventListener('cancel', this.#handleCancel);

    window.addEventListener('pagehide', this.#handlePageHide);
    window.addEventListener('pageshow', this.#handlePageShow);

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#handleResize);
    window.removeEventListener('pagehide', this.#handlePageHide);
    window.removeEventListener('pageshow', this.#handlePageShow);

    this.#dialog?.removeEventListener('close', this.#handleNativeClose);
    this.#dialog?.removeEventListener('cancel', this.#handleCancel);

    this.#closeImmediately();
    super.disconnectedCallback();
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;
    const width = window.innerWidth;

    if (
      (minWidth && width < minWidth) ||
      (maxWidth && width > maxWidth)
    ) {
      this.closeDialog();
    }
  }, 50);

  showDialog() {
    const dialog = this.#dialog;
    if (!dialog || dialog.open || this.#opening || this.#closing) return;

    this.#previousScrollY = getScrollTop();
    this.#opening = true;

    this.#openFrame = requestAnimationFrame(() => {
      this.#openFrame = null;
      this.#opening = false;

      if (!this.isConnected || dialog.open) return;

      try {
        lockScroll(dialog);
        dialog.showModal();

        this.#sessionOpen = true;
        this.addEventListener('click', this.#handleClick);
        this.addEventListener('keydown', this.#handleKeyDown);
        this.dispatchEvent(new DialogOpenEvent());
      } finally {
        // Never retain a lock if opening the dialog failed.
        if (!dialog.open) unlockScroll(dialog);
      }
    });
  }

  closeDialog = async () => {
    const dialog = this.#dialog;
    if (!dialog || this.#closing) return;

    if (!dialog.open) {
      this.#closeImmediately();
      return;
    }

    this.#closing = true;
    const version = ++this.#closeVersion;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    try {
      dialog.style.animation = 'none';
      void dialog.offsetWidth;
      dialog.classList.add('dialog-closing');
      dialog.style.animation = '';

      // Keep a finite fallback if an animation never settles.
      await Promise.race([
        onAnimationEnd(dialog, undefined, { subtree: false }),
        new Promise((resolve) => {
          this.#closeTimer = window.setTimeout(resolve, 1000);
        }),
      ]);
    } finally {
      if (version === this.#closeVersion) {
        this.#clearCloseTimer();

        try {
          if (dialog.open) dialog.close();
        } finally {
          this.#finishClose(true);
        }
      }
    }
  };

  toggleDialog = () => {
    if (this.#dialog?.open || this.#opening) {
      if (this.#opening) {
        this.#closeImmediately();
      } else {
        this.closeDialog();
      }
    } else {
      this.showDialog();
    }
  };

  #clearCloseTimer() {
    if (this.#closeTimer !== null) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }

  #finishClose(restorePosition = false) {
    const dialog = this.#dialog;
    if (!dialog) return;

    this.#clearCloseTimer();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.remove('dialog-closing');
    dialog.style.animation = '';
    this.#closing = false;
    this.#opening = false;

    unlockScroll(dialog);

    const wasOpen = this.#sessionOpen;
    this.#sessionOpen = false;

    if (wasOpen) {
      try {
        if (restorePosition && this.isConnected) {
          scrollTo({
            top: this.#previousScrollY,
            behavior: 'instant',
          });
        }
      } finally {
        this.dispatchEvent(new DialogCloseEvent());
      }
    }
  }

  #closeImmediately() {
    ++this.#closeVersion;

    if (this.#openFrame !== null) {
      cancelAnimationFrame(this.#openFrame);
      this.#openFrame = null;
    }

    const dialog = this.#dialog;

    try {
      if (dialog?.open) dialog.close();
    } finally {
      this.#finishClose(false);
    }
  }

  #handleNativeClose = () => {
    // A delayed close event must not unlock a newly reopened dialog.
    if (this.#dialog?.open) return;

    ++this.#closeVersion;
    this.#finishClose(true);
  };

  #handleCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  #handlePageHide = () => {
    this.#closeImmediately();
  };

  #handlePageShow = (event) => {
    if (event.persisted) this.#closeImmediately();
  };

  #handleClick = (event) => {
    const dialog = this.#dialog;
    if (dialog?.open && isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  };

  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) {
  customElements.define('dialog-component', DialogComponent);
}

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (
      event.target instanceof HTMLDetailsElement &&
      event.target.hasAttribute('scroll-lock')
    ) {
      if (event.target.open) {
        lockScroll(event.target);
      } else {
        unlockScroll(event.target);
      }
    }
  },
  { capture: true }
);
